import { useMemo, useState } from "react";

import { Button } from "components/ui/Button";
import { Dialog, DialogFooter } from "components/ui/Dialog";
import { Select } from "components/ui/Select/Select";
import { usePatientData } from "features/patients/hooks/usePatientData";
import {
  AUDIT_C_QUESTIONS,
  calculateAuditC,
  calculateFullAudit,
  FULL_AUDIT_QUESTIONS,
  getAuditCRiskLevel,
  needsFullAudit,
} from "utils/auditC";

import styles from "./AuditCCalculatorModal.module.css";

const isAnswered = (value) => value !== null && value !== undefined;

const isNeverValue = (value) => isAnswered(value) && Number(value) === 0;

export const AuditCCalculatorModal = ({
  isOpen,
  onClose,
  onConfirm,
  prefillQ1,
  prefillQ2,
  prefillQ3,
  prefillQ4,
  prefillQ5,
  prefillQ6,
  prefillQ7,
  prefillQ8,
  prefillQ9,
  prefillQ10,
}) => {
  const { data: patient } = usePatientData();

  const [q1, setQ1] = useState(prefillQ1 ?? null);
  const [q2, setQ2] = useState(prefillQ2 ?? null);
  const [q3, setQ3] = useState(prefillQ3 ?? null);
  const [q4, setQ4] = useState(prefillQ4 ?? null);
  const [q5, setQ5] = useState(prefillQ5 ?? null);
  const [q6, setQ6] = useState(prefillQ6 ?? null);
  const [q7, setQ7] = useState(prefillQ7 ?? null);
  const [q8, setQ8] = useState(prefillQ8 ?? null);
  const [q9, setQ9] = useState(prefillQ9 ?? null);
  const [q10, setQ10] = useState(prefillQ10 ?? null);
  const [showErrors, setShowErrors] = useState(false);

  const isNeverDrinker = isNeverValue(q1);

  const auditCScore = useMemo(() => {
    const values = isNeverDrinker ? [q1, 0, 0] : [q1, q2, q3];

    return calculateAuditC(...values);
  }, [isNeverDrinker, q1, q2, q3]);

  const showFullAudit = useMemo(
    () => needsFullAudit(auditCScore, patient?.gender),
    [auditCScore, patient?.gender]
  );

  const fullAuditScore = useMemo(() => {
    if (!showFullAudit) return null;

    return calculateFullAudit(q1, q2, q3, q4, q5, q6, q7, q8, q9, q10);
  }, [showFullAudit, q1, q2, q3, q4, q5, q6, q7, q8, q9, q10]);

  const risk = useMemo(
    () => (auditCScore != null ? getAuditCRiskLevel(auditCScore, patient?.gender) : null),
    [auditCScore, patient?.gender]
  );

  const resultColorClass =
    risk?.color === "green"
      ? styles.resultLow
      : risk?.color === "orange"
      ? styles.resultWarning
      : styles.resultHigh;

  const scoreColorClass =
    risk?.color === "green"
      ? styles.scoreLow
      : risk?.color === "orange"
      ? styles.scoreWarning
      : styles.scoreHigh;

  const handleClose = () => {
    setShowErrors(false);
    onClose();
  };

  const handleConfirm = () => {
    const auditCQuestionsAnswered = isNeverDrinker
      ? isAnswered(q1)
      : [q1, q2, q3].every(isAnswered);

    const fullAuditQuestionsAnswered =
      !showFullAudit || [q4, q5, q6, q7, q8, q9, q10].every(isAnswered);

    if (!auditCQuestionsAnswered || !fullAuditQuestionsAnswered) {
      setShowErrors(true);
      return;
    }

    // When the full AUDIT is completed, the alcohol risk score takes the higher
    // of the two scores (full AUDIT always >= AUDIT-C since it includes Q1–Q3).
    const effectiveScore =
      fullAuditScore != null
        ? Math.max(Number(auditCScore), Number(fullAuditScore))
        : auditCScore;

    setShowErrors(false);
    onConfirm(
      String(effectiveScore),
      q1, isNeverDrinker ? 0 : q2, isNeverDrinker ? 0 : q3,
      fullAuditScore != null ? String(fullAuditScore) : null,
      q4, q5, q6, q7, q8, q9, q10
    );
    handleClose();
  };

  const auditCEntries = Object.entries(AUDIT_C_QUESTIONS);
  const fullAuditEntries = Object.entries(FULL_AUDIT_QUESTIONS);

  const auditCValues = [q1, q2, q3];
  const auditCSetters = [setQ1, setQ2, setQ3];

  const fullAuditValues = [q4, q5, q6, q7, q8, q9, q10];
  const fullAuditSetters = [setQ4, setQ5, setQ6, setQ7, setQ8, setQ9, setQ10];

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      title="Alcohol Risk Score"
      width={showFullAudit ? 1160 : 920}
    >
      <div className={styles.body}>
        <div className={styles.section}>
          <span className={styles.sectionTitle}>AUDIT-C Screening (Q1–Q3)</span>
          <div className={styles.questionGrid}>
            {auditCEntries.map(([key, { label, options }], index) => {
              if (index !== 0 && isNeverDrinker) return null;

              const value = auditCValues[index];
              const setter = auditCSetters[index];
              const hasError = showErrors && !isAnswered(value);

              return (
                <div key={key}>
                  <span className={styles.questionLabel}>{label}</span>
                  <Select
                    options={options.map((o) => ({ label: o.label, value: o.value }))}
                    value={value}
                    error={hasError}
                    onChange={(val) => {
                      setter(val);

                      if (index === 0 && isNeverValue(val)) {
                        setQ2(0);
                        setQ3(0);
                      }

                      setShowErrors(false);
                    }}
                  />
                  {!!hasError && (
                    <p className={styles.fieldError}>Please select an answer</p>
                  )}
                </div>
              );
            })}
          </div>

          {auditCScore != null && risk && (
            <div className={`${styles.result} ${resultColorClass}`}>
              <div>
                <div className={styles.resultLabel}>{risk.level}</div>
                <p className={styles.resultDescription}>AUDIT-C score</p>
              </div>
              <div className={`${styles.resultScore} ${scoreColorClass}`}>
                {auditCScore}/12
              </div>
            </div>
          )}
        </div>

        {!!showFullAudit && (
          <>
            <div className={styles.divider} />
            <div className={styles.section}>
              <span className={styles.sectionTitle}>Full AUDIT — Additional Questions (Q4–Q10)</span>
              <div className={styles.questionGrid}>
                {fullAuditEntries.map(([key, { label, options }], index) => {
                  const value = fullAuditValues[index];
                  const setter = fullAuditSetters[index];
                  const hasError = showErrors && !isAnswered(value);

                  return (
                    <div key={key}>
                      <span className={styles.questionLabel}>{label}</span>
                      <Select
                        options={options.map((o) => ({ label: o.label, value: o.value }))}
                        value={value}
                        error={hasError}
                        onChange={(val) => {
                          setter(val);
                          setShowErrors(false);
                        }}
                      />
                      {!!hasError && (
                        <p className={styles.fieldError}>Please select an answer</p>
                      )}
                    </div>
                  );
                })}
              </div>

              {fullAuditScore != null && (
                <div className={`${styles.result} ${styles.resultHigh}`}>
                  <div>
                    <div className={styles.resultLabel}>Full AUDIT Score</div>
                    <p className={styles.resultDescription}>Total across all 10 questions</p>
                  </div>
                  <div className={`${styles.resultScore} ${styles.scoreHigh}`}>
                    {fullAuditScore}/40
                  </div>
                </div>
              )}
            </div>
          </>
        )}

      </div>
      <DialogFooter>
        <Button variant="outline" onClick={handleClose}>
          Cancel
        </Button>
        <Button onClick={handleConfirm}>Use score</Button>
      </DialogFooter>
    </Dialog>
  );
};
